import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Play,
  Pause,
  RotateCcw,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  User,
  MessageSquare,
  ThumbsUp,
  Loader2,
  Settings
} from 'lucide-react';
import { simulatorAPI, plannerAPI } from '../api';

export default function Simulator() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const calendarId = searchParams.get('calendarId');
  const itemId = searchParams.get('itemId');

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [speed, setSpeed] = useState(1); // 1x, 2x, 5x
  const [visibleReplies, setVisibleReplies] = useState(0);
  const [expandedReplies, setExpandedReplies] = useState({});
  const intervalRef = useRef(null);

  // Fetch calendar item
  const { data: item, isLoading: itemLoading } = useQuery({
    queryKey: ['calendarItem', calendarId, itemId],
    queryFn: async () => {
      const res = await plannerAPI.getCalendar(calendarId);
      return res.data.items.find(i => i.id === itemId);
    },
    enabled: !!calendarId && !!itemId
  });

  // Run simulation mutation
  const simulateMutation = useMutation({
    mutationFn: () => simulatorAPI.simulate(calendarId, itemId),
    onSuccess: (res) => {
      toast.success('Simulation generated');
    }
  });

  // Simulation data (either from mutation or item's existing simulation or real followups)
  const simulation = simulateMutation.data?.data || item?.simulation || {
    threadReplies: item?.followups?.length > 0 ? item.followups.map((f, idx) => ({
      id: f.id || `reply-${idx}`,
      author: f.username || f.author || 'User',
      content: f.comment_text || f.content || '',
      minute: (idx + 1) * 3, // Space replies 3 minutes apart
      depth: idx === 0 ? 0 : Math.min(idx % 3, 2),
      upvotes: Math.floor(Math.random() * 30) + 5,
      sentiment: f.score >= 7 ? 'positive' : f.score >= 5 ? 'neutral' : 'negative',
      isFollowup: true, // All followups are from personas
      persona_id: f.persona_id
    })) : [],
    flags: [],
    upvotes: Math.floor(Math.random() * 100) + 20
  };

  // Timer effect
  useEffect(() => {
    if (isPlaying && simulation?.threadReplies?.length > 0) {
      intervalRef.current = setInterval(() => {
        setCurrentTime(prev => {
          const nextTime = prev + 1;
          // Show more replies based on time
          const repliesAtTime = simulation.threadReplies.filter(
            r => r.minute <= nextTime
          ).length;
          setVisibleReplies(repliesAtTime);
          
          // Stop at end
          const maxTime = Math.max(...simulation.threadReplies.map(r => r.minute));
          if (nextTime > maxTime + 5) {
            setIsPlaying(false);
            return maxTime;
          }
          return nextTime;
        });
      }, 1000 / speed);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, speed, simulation]);

  const handlePlay = () => {
    if (!simulation?.threadReplies?.length) {
      simulateMutation.mutate();
    }
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    setVisibleReplies(0);
    setExpandedReplies({});
  };

  const toggleReply = (replyId) => {
    setExpandedReplies(prev => ({
      ...prev,
      [replyId]: !prev[replyId]
    }));
  };

  if (itemLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-reddit-orange" />
      </div>
    );
  }

  if (!item && itemId) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Item not found</p>
        <button 
          onClick={() => navigate(-1)} 
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-full hover:bg-gray-300 transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  const visibleThreadReplies = simulation?.threadReplies?.slice(0, visibleReplies) || [];

  return (
    <div className="w-full max-w-4xl mx-auto px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Thread Simulator</h1>
            <p className="text-sm text-gray-500">
              Visualize how the conversation might unfold
            </p>
          </div>
        </div>

        {/* Playback Controls */}
        <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 p-2 self-start sm:self-auto">
          <button
            onClick={handleReset}
            className="p-2 hover:bg-gray-100 rounded"
            title="Reset"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
          
          {isPlaying ? (
            <button
              onClick={handlePause}
              className="p-2 bg-reddit-orange text-white rounded hover:bg-reddit-orangeLight"
            >
              <Pause className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handlePlay}
              className="p-2 bg-reddit-orange text-white rounded hover:bg-reddit-orangeLight disabled:opacity-50"
              disabled={simulateMutation.isPending}
            >
              {simulateMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Play className="w-5 h-5" />
              )}
            </button>
          )}

          <select
            value={speed}
            onChange={(e) => setSpeed(Number(e.target.value))}
            className="input py-1.5 text-sm"
          >
            <option value={1}>1x</option>
            <option value={2}>2x</option>
            <option value={5}>5x</option>
          </select>

          <div className="flex items-center gap-1.5 text-sm text-gray-600 pl-2 pr-3 min-w-[70px]">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span className="font-mono">{currentTime}:{(currentTime % 1 * 60).toFixed(0).padStart(2, '0')}</span>
          </div>
        </div>
      </div>

      {/* Flags */}
      {simulation?.flags?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 text-yellow-800 font-medium mb-2">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span>Potential Issues Detected</span>
          </div>
          <ul className="space-y-1">
            {simulation.flags.map((flag, idx) => (
              <li key={idx} className="text-sm text-yellow-700 flex items-start">
                <span className="mr-2">•</span>
                {flag}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Original Post */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
        <div className="bg-reddit-orange text-white px-3 sm:px-4 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
          <span className="font-medium">r/{item?.subreddit?.replace('r/', '') || 'subreddit'}</span>
          <span className="text-xs sm:text-sm opacity-75">Posted by u/{item?.author_username || item?.persona_id || 'user'}</span>
        </div>
        <div className="p-3 sm:p-4">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
            {item?.title || 'Post Title'}
          </h2>
          <p className="text-sm sm:text-base text-gray-700 whitespace-pre-wrap">
            {item?.body || item?.content || 'No content available'}
          </p>
          <div className="flex items-center gap-3 sm:gap-4 mt-4 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <ThumbsUp className="w-4 h-4" />
              <span>{simulation?.upvotes || Math.floor(Math.random() * 100) + 20}</span>
            </div>
            <div className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              <span>{item?.followups?.length || simulation?.threadReplies?.length || 0} replies</span>
            </div>
          </div>
        </div>
      </div>

      {/* Thread Replies */}
      <div className="space-y-2">
        {visibleThreadReplies.map((reply, idx) => (
          <div
            key={reply.id || idx}
            className={`
              bg-white rounded-lg border p-3 sm:p-4 transition-all
              ${reply.isFollowup ? 'border-reddit-orange bg-orange-50' : 'border-gray-200'}
              ${reply.flagged ? 'ring-2 ring-yellow-400' : ''}
            `}
            style={{ marginLeft: `${Math.min(reply.depth * 16, 48)}px` }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-2">
              <div className="flex items-center flex-wrap gap-2">
                <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="font-medium text-gray-700 text-sm sm:text-base">
                  {reply.isFollowup ? (
                    <span className="text-reddit-orange">u/{reply.author} (Persona)</span>
                  ) : (
                    `u/${reply.author}`
                  )}
                </span>
                <span className="text-xs text-gray-400">• +{reply.minute} min</span>
              </div>
              {reply.flagged && (
                <span className="flex items-center text-yellow-600 text-xs flex-shrink-0">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Flagged
                </span>
              )}
            </div>
            
            <p className="text-sm sm:text-base text-gray-700 whitespace-pre-wrap">
              {expandedReplies[reply.id] || reply.content.length <= 200
                ? reply.content
                : `${reply.content.substring(0, 200)}...`
              }
            </p>
            
            {reply.content.length > 200 && (
              <button
                onClick={() => toggleReply(reply.id)}
                className="text-sm text-reddit-orange hover:underline mt-1 inline-flex items-center"
              >
                {expandedReplies[reply.id] ? (
                  <>Show less <ChevronUp className="w-3 h-3 ml-1" /></>
                ) : (
                  <>Show more <ChevronDown className="w-3 h-3 ml-1" /></>
                )}
              </button>
            )}

            <div className="flex items-center flex-wrap gap-2 sm:gap-3 mt-2 text-xs text-gray-500">
              <span className="flex items-center">
                <ThumbsUp className="w-3 h-3 mr-1" />
                {reply.upvotes || 0}
              </span>
              {reply.sentiment && (
                <span className={`
                  px-1.5 py-0.5 rounded
                  ${reply.sentiment === 'positive' ? 'bg-green-100 text-green-700' :
                    reply.sentiment === 'negative' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'}
                `}>
                  {reply.sentiment}
                </span>
              )}
            </div>
          </div>
        ))}

        {visibleReplies === 0 && !isPlaying && (
          <div className="text-center py-12 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Press Play to start the simulation</p>
            <p className="text-sm mt-1">Watch the thread unfold in real-time</p>
          </div>
        )}
      </div>

      {/* Status */}
      {visibleReplies > 0 && visibleReplies === simulation?.threadReplies?.length && (
        <div className="mt-6 flex items-center justify-center text-green-600">
          <CheckCircle className="w-5 h-5 mr-2" />
          Simulation complete
        </div>
      )}
    </div>
  );
}
